import React, { useEffect, useMemo, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Grid from '@material-ui/core/Grid'
import Link from '@material-ui/core/Link'
import InputAdornment from '@material-ui/core/InputAdornment'
import FormHelperText from '@material-ui/core/FormHelperText'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { BACPersonalizationData } from '../Models/BACPersonalizationData'
import { BACGreetingCardData } from '../Models/BACGreetingCardData'
import { BACCarrierOptionData } from '../Models/BACCarrierOptionData'
import Incrementer from '../../Base/Incrementer'
import {
    collectFormErrors,
    getErrorMessage,
    inputHasError,
    removeErrorFromPath,
} from '../../../utils/formErrorUtils'
import IntroContent from './IntroContent'
import Hidden from '@material-ui/core/Hidden'
import { createACardLandingFormSchema } from '../../formValidationSchemas'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import {
    MAX_LOAD_VALUE,
    MIN_LOAD_VALUE,
    STEP_REVIEW,
} from '../../../containers/CreateACard'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import trimStart from 'lodash/trimStart'
import { DEFAULT_LOAD_VALUE, CARD_TYPE, CARRIER_TYPE } from '../../../constants'
import {
    requestNextStep,
    setCustomBuildACardData,
} from '../../../actions/createACard'
import { useQueryParam } from '../../../hooks/useQueryParam'
import ApplyAllCheckbox from '../Common/ApplyAllCheckbox'
import { BACCardData } from '../Models/BACCardData'
import DiscardItemAndReturnToReview from '../DiscardItemAndReturnToReview'
import BacCardValueSelector from './BacCardValueSelector'
import ShippingInfoText from './ShippingInfoText'

const useStyles = makeStyles((theme) => ({
    '@global': {
        '.MuiInputBase-input::placeholder': {
            fontSize: '16px',
        },
    },
    cardPreviewWrapperContainerOnGreetingCard: {
        position: 'relative',
        maxWidth: '140px',
        top: '-80px',
        left: '52%',
        [theme.breakpoints.up('md')]: {
            left: '60%',
        },
    },
    greetingCardPreview: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        maxWidth: '169px',
        maxHeight: '247px',
        marginTop: '3rem',
        marginBottom: '3rem',
    },
    labelStyle: {
        color: '#3B1A6E',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: '800',
        lineHeight: 'normal',
        marginBottom: '1.5rem',
        alignSelf: 'flex-start',
        marginTop: '1.25rem',
    },
    packageMethodSelector: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '1.5rem',
    },
    packageTypeSelectorOption: {
        cursor: 'pointer',
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        aspectRatio: 3,
        alignSelf: 'center',
        border: `2px solid #D3D3D3`,
        borderRadius: 75,
        '&.selected': {
            backgroundColor: '#6E1A6A',
            color: 'white',
            zIndex: 1,
            '& $usageText': {
                color: 'white',
            },
        },
        height: '55px',
        [theme.breakpoints.up('md')]: {
            width: '40%',
            maxWidth: '40%',
            minWidth: 'auto',
        },
    },
    packageMethodText: {
        fontSize: '16px',
        fontWeight: '600',
    },
    carrierPreviewWrapper: {
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        boxShadow: `0 0.3px 0.9px rgba(0, 0, 0, 0.037),
            0 0.8px 2.2px rgba(0, 0, 0, 0.053),
            0 1.5px 4.1px rgba(0, 0, 0, 0.065),
            0 2.7px 7.4px rgba(0, 0, 0, 0.077),
            0 5px 13.8px rgba(0, 0, 0, 0.093),
            0 12px 33px rgba(0, 0, 0, 0.13)`,
    },
    incrementerContainer: { display: 'flex', alignItems: 'center' },
    incrementerText: {
        textTransform: 'none',
    },
    textInput: {
        width: '100%',
        maxWidth: '470px',
        backgroundColor: '#FFF',
        borderRadius: '8px',
        height: '55px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(32, 32, 32, 0.5)',
        },
    },
    nextButton: {
        textTransform: 'none',
        width: '100%',
        maxWidth: '450px',
        color: '#FFF',
        marginTop: '24px',
        marginBottom: '24px',
        backgroundColor: '#592063',
        fontSize: '16px',
        fontWeight: '700',
        borderRadius: '81px',
        border: '3px solid #592063',
        height: '66px',
        '&:hover': {
            backgroundColor: '#592063',
            border: '3px solid #592063',
        },
    },
    bulkLink: (props) => {
        return {
            fontSize: '0.875rem',
            fontWeight: 'bold',
            color: '#005C58',
            textDecorationThickness: 2,
            cursor: 'pointer',
            marginTop: '0.5rem',
            marginLeft: '0px',
            alignSelf: props.quantityError ? 'flex-start' : 'unset',
            lineHeight: 1,
            [theme.breakpoints.up(371)]: {
                lineHeight: '1.2',
            },
        }
    },
    quantityInfoAlert: {
        backgroundColor: '#FFFFFF',
        border: '2px solid #CCC',
        borderRadius: '8px',
        marginTop: '0.5rem',
        color: '#505E71',
        fontWeight: 'bold',
        maxWidth: '340px',
    },
    quantityInfoAlertIcon: {
        fontSize: '1.5rem',
        color: '#005C58 !important',
    },
    quantityIncrementorContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem',
    },
    applyAllCheckboxContainer: {
        marginTop: '1.5rem',
    },
    straightLine: {
        margin: '1rem 0',
        [theme.breakpoints.up('md')]: {
            margin: '1.5rem 0',
        },
    },
    numberInputField: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    inputSlider: {
        textAlign: 'center',
        width: '7rem',
        borderRadius: '5px',
        border: '3px solid #CCC',
        padding: 0,
        background: '#F4F4F4',
        fontSize: '40px',
        color: '#3B1A6E',
        fontWeight: '700',
    },
    bacSlider: {
        width: '70%',
    },
    bacSliderLimits: {
        color: '#005C59',
        fontSize: '1rem',
        fontWeight: '700',
    },
    errorText: {
        marginTop: '0.25rem',
        marginBottom: '0.5rem',
        color: theme.palette.error.main,
        fontSize: '0.75rem',
        [theme.breakpoints.up('md')]: {
            fontSize: '0.85rem',
        },
    },
    imageGuidelinesText: {
        color: '#505E71',
        fontSize: '17px',
        lineHeight: '17px',
        fontWeight: '400',
        margin: '2rem 0 1rem 0',
    },
    imageGuidelinesLink: {
        textDecoration: 'underline',
        color: '#6E1A6A',
    },
    carrierOptionLabel: {
        '& button:first-child': {
            borderTopLeftRadius: '200px',
            borderBottomLeftRadius: '200px',
            paddingRight: '2rem',
            '& span': {
                marginRight: '1rem',
            },
            fontSize: '0.875rem',
            lineHeight: '0.875rem',
            [theme.breakpoints.up(421)]: {
                fontSize: '1.25rem',
                lineHeight: '1.25rem',
            },
        },
        '& button:last-child': {
            borderTopRightRadius: '200px',
            borderBottomRightRadius: '200px',
            '& span': {
                marginLeft: '1rem',
            },
            fontSize: '0.875rem',
            lineHeight: '0.875rem',
            [theme.breakpoints.up(421)]: {
                fontSize: '1.25rem',
                lineHeight: '1.25rem',
            },
        },
    },
    loadingCardDesignsContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '160px',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#3B1A6E',
    },
    errorLoadingDesignsAlert: {
        fontSize: '1rem',
        margin: '1rem 0',
    },
    listItems: {
        color: '#3B1A6E',
        fontWeight: '800',
        lineHeight: '2rem',
        margin: '2rem 0',
        paddingLeft: '0.75rem',
        maxWidth: '350px',
        width: '100%',
        '& li::marker': {
            color: '#3B1A6E',
        },
        '& li': { width: '100%' },
        '& li span': {
            fontSize: '1rem',
        },
    },
    carrierTypeDescriptionStyles: {
        color: '#505E71',
        fontSize: '14px',
        lineHeight: '17px',
        fontWeight: '400',
        marginBottom: 25,
    },
    customizeText: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#202020',
        letterSpacing: '-0.5px',
        marginBottom: '8px',
        marginTop: '24px',
    },
    chooseAmountText: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#202020',
        letterSpacing: '-0.5px',
        marginTop: '1.5rem',
        marginBottom: '0.5rem',
    },
    chooseQuantityText: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#202020',
        letterSpacing: '-0.5px',
        marginTop: '24px',
        marginBottom: '8px',
    },
    chooseShippingText: {
        fontSize: '1rem',
        fontWeight: 800,
        marginTop: '1.75rem',
        marginBottom: '0.5rem',
        paddingBottom: '0.5rem',
        color: 'rgb(77,77,77)',
    },
    shippingPackageButton: {
        background: '#F4F4F4',
        borderRadius: 16,
        border: '3px solid rgb(177,177,177)',
        padding: '1rem 1.5rem',
    },
    shippingPackageCost: {
        color: '#7347B2',
        margin: 0,
        fontWeight: 'bold',
        fontSize: '1rem',
        textTransform: 'uppercase',
    },
    shippingPackageType: {
        margin: '10px 0 0 0',
        fontWeight: 'bold',
    },
    shippingPackageSize: { margin: 0 },
    shippingPackageCopy: { margin: '10px 0 0 0' },
    bacLandingPlaceholders: {
        marginLeft: '8px',
        '&::placeholder': {
            marginLeft: '8px',
        },
    },
    bulkBuyCta: { margin: '0 0 0 15px', fontSize: '13px' },
    bulkBuyCtaError: {
        position: 'relative',
    },
    bulkBuyLink: {
        color: '#424242',
        textDecoration: 'underline',
        fontWeight: 'bold',
    },
}))

const DEFAULT_CARD_DENOMINATIONS = [10, 20, 30, 50, 100, 200, 250, 500]

const CustomCardForm = ({
    applyCardImageToAll,
    baseUrl,
    cardNetwork,
    cloudinaryId,
    defaultCardNetwork,
    defaultDeliveryMethod,
    deliveryMethod,
    isVirtual,
    messageOnCard,
    nameOnCard,
    selectedCardDesign,
    setImageError,
    setMessageOnCard,
    setNameOnCard,
    setQuantity,
    amount,
    setAmount,
    showApplyAllCheckbox,
    quantity,
    imageErrorRef,
    urlData,
}) => {
    const [errors, setErrors] = useState({})
    const classes = useStyles({
        quantityError: getErrorMessage(errors, 'quantity'),
    })
    const dispatch = useDispatch()
    const query = useQueryParam()
    const denomQueryParam = query.get('denom')
    const embossRegex = /[^-a-zA-Z0-9-\s]/
    const denominationInputRef = useRef(null)
    const nameOnCardRef = useRef(null)
    const messageOnCardRef = useRef(null)
    const hasUrlData = urlData && Object.values(urlData).some((v) => v)

    const { customCards, maxQuantity, activeCard, isEditing } = useSelector(
        (state) => state.createACard
    )

    const [movingToNextStep, setMovingToNextStep] = useState(false)
    const [fieldInFocus, setFieldInFocus] = useState('')

    const [carrierType, setCarrierType] = useState(
        activeCard?.personalizationInfo?.carrierType
            ? activeCard?.personalizationInfo?.carrierType
            : CARRIER_TYPE.GREETING_CARD
    )

    const [applyCardDetailsToAll, setApplyCardDetailsToAll] = useState(
        !isEditing
    )
    const showQuantitySelector = useMemo(() => {
        return isNull(activeCard)
    }, [activeCard])

    const onDenominationChange = (denomination) => {
        const parsedAmount = parseInt(denomination)
        if (isNaN(parsedAmount)) {
            setAmount(0)
            validateField('amount', 0)
            return
        }
        setAmount(parsedAmount)
        validateField('amount', parsedAmount)
    }

    /**
     * Set amount from query param if it exists and is valid
     */
    useEffect(() => {
        const validateAndSetDenomination = (denomination) => {
            const parsedDenomination = parseInt(denomination)

            if (
                !isNaN(parsedDenomination) &&
                parsedDenomination >= MIN_LOAD_VALUE &&
                parsedDenomination <= MAX_LOAD_VALUE
            ) {
                setAmount(parsedDenomination)
            }
        }

        if (!isEmpty(denomQueryParam)) {
            validateAndSetDenomination(denomQueryParam)
        }
    }, [denomQueryParam, setAmount])

    async function validateLandingForm(
        amount,
        quantity,
        nameOnCard,
        messageOnCard
    ) {
        return await createACardLandingFormSchema(isVirtual).validate(
            {
                amount: amount,
                quantity: quantity,
                nameOnCard: nameOnCard,
                messageOnCard: messageOnCard,
            },
            { abortEarly: false }
        )
    }

    async function validateFormAt(path, data) {
        return await createACardLandingFormSchema(isVirtual).validateAt(
            path,
            data,
            {
                abortEarly: false,
            }
        )
    }

    const validateField = (path, data) => {
        validateFormAt(path, { [path]: data })
            .then(function (response) {
                setErrors(removeErrorFromPath(path, errors))
            })
            .catch(function (e) {
                collectFormErrors(e, errors, setErrors)
                //e?.inner is an array of errors
                if (e?.inner && Array.isArray(e.inner)) {
                    //errorsByField is an object with the field name as key and the error message as value
                    const errorsByField = e.inner.reduce(
                        (errorsByField, curr) => {
                            errorsByField[curr.path] = curr.message
                            return errorsByField
                        },
                        //initially an empty object
                        {}
                    )
                    if (errorsByField.nameOnCard) {
                        nameOnCardRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    } else if (errorsByField.messageOnCard) {
                        messageOnCardRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    }
                }
            })
    }

    const isQuantityLimitExceeded = () => {
        if (isNull(activeCard)) {
            return quantity + customCards.length > maxQuantity
        }

        return customCards.length > maxQuantity
    }
    const onFocus = (fieldName) => {
        return () => {
            setFieldInFocus(fieldName)
        }
    }

    const onBlur = (fieldName) => {
        return () => {
            if (fieldInFocus === fieldName) {
                setFieldInFocus('')
            }
        }
    }
    const handleCustomCardFormSubmit = async () => {
        // This prevents from there being a custom image added to the
        // buildACardData object if a predesign is selected.
        if (selectedCardDesign) {
            cloudinaryId = undefined
        }

        // validate they selected an image
        if (!cloudinaryId && !selectedCardDesign) {
            setImageError('Please upload a photo or select a design')
            imageErrorRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
            return
        }
        // validate form fields
        validateLandingForm(amount, quantity, nameOnCard, messageOnCard)
            .then(() => {
                const buildACardData = new BACCardData(
                    parseFloat(amount),
                    applyCardDetailsToAll,
                    applyCardImageToAll,
                    cardNetwork,
                    cloudinaryId,
                    selectedCardDesign,
                    DEFAULT_LOAD_VALUE,
                    defaultCardNetwork,
                    defaultDeliveryMethod,
                    deliveryMethod,
                    messageOnCard,
                    nameOnCard,
                    quantity
                )
                dispatch(setCustomBuildACardData(buildACardData))

                setMovingToNextStep(true)
            })
            .catch(function (e) {
                console.error(e)
                collectFormErrors(e, errors, setErrors)
                if (e?.inner && Array.isArray(e.inner)) {
                    const errorsByField = e.inner.reduce(
                        (errorsByField, curr) => {
                            errorsByField[curr.path] = curr.message
                            return errorsByField
                        },
                        {}
                    )
                    if (errorsByField.nameOnCard) {
                        nameOnCardRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    } else if (errorsByField.messageOnCard) {
                        messageOnCardRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    }
                }
            })
    }

    useEffect(() => {
        if (movingToNextStep && activeCard) {
            if (carrierType !== activeCard?.personalizationInfo?.carrierType) {
                const carrierTo = activeCard?.personalizationInfo?.carrierTo
                const carrierFrom = activeCard?.personalizationInfo?.carrierFrom
                const recipientEmail =
                    activeCard?.personalizationInfo?.recipientEmail
                const carrierMessage =
                    activeCard?.personalizationInfo?.carrierMessage

                if (applyCardDetailsToAll) {
                    const data = new BACPersonalizationData(
                        null,
                        null,
                        null,
                        urlData.carrier_type || carrierType,
                        null,
                        true
                    )
                    dispatch(setCustomBuildACardData(data))
                }

                const data = new BACPersonalizationData(
                    carrierTo,
                    urlData.message || carrierMessage,
                    carrierFrom,
                    urlData.carrier_type,
                    recipientEmail,
                    urlData.message ? applyCardDetailsToAll : null
                )

                dispatch(setCustomBuildACardData(data))
                let greetingCardImageData = null
                if (urlData.slug && urlData.cloudinary_id) {
                    greetingCardImageData = {
                        slug: urlData.slug,
                        cloudinary_id: urlData.cloudinary_id,
                    }
                }
                const greetingCardData = new BACGreetingCardData(
                    greetingCardImageData || null,
                    greetingCardImageData ? applyCardDetailsToAll : null
                )
                dispatch(setCustomBuildACardData(greetingCardData))

                const carrierOptionData = new BACCarrierOptionData(
                    urlData.carrier_type || null,
                    urlData.carrier_type ? applyCardDetailsToAll : null
                )
                dispatch(setCustomBuildACardData(carrierOptionData))
            }

            setMovingToNextStep(false)
            dispatch(requestNextStep(true))
        }
    }, [
        activeCard,
        carrierType,
        deliveryMethod,
        dispatch,
        movingToNextStep,
        applyCardDetailsToAll,
        urlData.carrier_type,
        urlData.cloudinary_id,
        urlData.message,
        urlData.slug,
    ])

    return (
        <>
            {!isVirtual && (
                <>
                    <p className={classes.customizeText}>
                        3. Customize text on card:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <TextField
                            id="name-on-card"
                            variant="outlined"
                            label={
                                <>
                                    <span style={{ verticalAlign: 'middle' }}>
                                        Recipient name
                                    </span>
                                    <span
                                        style={{
                                            color: 'rgba(255, 58, 58, 0.50)',
                                            fontSize: '24px',
                                            fontWeight: '700',
                                            marginLeft: 2,
                                            verticalAlign: 'middle',
                                            display: 'inline-block',
                                            lineHeight: 1,
                                        }}
                                    >
                                        *
                                    </span>
                                </>
                            }
                            className={classes.textInput}
                            error={
                                fieldInFocus !== 'nameOnCard' &&
                                inputHasError(errors, 'nameOnCard')
                            }
                            helperText={
                                fieldInFocus !== 'nameOnCard' &&
                                getErrorMessage(errors, 'nameOnCard')
                            }
                            onFocus={onFocus('nameOnCard')}
                            onBlur={onBlur('nameOnCard')}
                            value={nameOnCard}
                            onChange={(e) => {
                                const newValue = trimStart(
                                    e.currentTarget.value.toUpperCase()
                                )
                                setNameOnCard(newValue)
                                validateField('nameOnCard', newValue)
                            }}
                            inputProps={{
                                maxLength: 21,
                                classes: {
                                    notchedOutline: classes.notchedOutline,
                                },
                                ref: nameOnCardRef,
                            }}
                            InputProps={{
                                classes: {
                                    input: classes.bacLandingPlaceholders,
                                },
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        style={{
                                            color: 'rgba(204, 204, 204, 0.8)',
                                        }}
                                    >
                                        {nameOnCard.length}
                                        /20
                                    </InputAdornment>
                                ),
                            }}
                            style={{
                                marginBottom: errors.nameOnCard
                                    ? '2.5rem'
                                    : '16px',
                            }}
                        />
                        <TextField
                            id="message-on-card"
                            variant="outlined"
                            label={'Gift message (optional)'}
                            className={classes.textInput}
                            error={
                                fieldInFocus !== 'messageOnCard' &&
                                inputHasError(errors, 'messageOnCard')
                            }
                            helperText={
                                fieldInFocus !== 'messageOnCard' &&
                                getErrorMessage(errors, 'messageOnCard')
                            }
                            onFocus={onFocus('messageOnCard')}
                            onBlur={onBlur('messageOnCard')}
                            value={messageOnCard}
                            onChange={(e) => {
                                const newValue = trimStart(
                                    e.currentTarget.value
                                        .toUpperCase()
                                        .replace(embossRegex, '')
                                )
                                setMessageOnCard(newValue)
                                validateField('messageOnCard', newValue)
                            }}
                            inputProps={{
                                maxLength: 21,
                                ref: messageOnCardRef,
                            }}
                            InputProps={{
                                classes: {
                                    input: classes.bacLandingPlaceholders,
                                },
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        style={{
                                            color: 'rgba(204, 204, 204, 0.8)',
                                        }}
                                    >
                                        {messageOnCard.length}
                                        /20
                                    </InputAdornment>
                                ),
                            }}
                            style={{
                                marginBottom: errors.messageOnCard
                                    ? '16px'
                                    : '0',
                            }}
                        />
                    </div>
                </>
            )}

            <p className={classes.chooseAmountText}>
                {isVirtual ? '3' : '4'}. Choose amount{' '}
                <span style={{ color: '#FF3A3A' }}>*</span>:
            </p>

            <BacCardValueSelector
                fieldName="amount"
                defaultCardValues={DEFAULT_CARD_DENOMINATIONS}
                value={amount}
                onValueChange={onDenominationChange}
                errors={errors}
                inputRef={denominationInputRef}
                isCurrency
            />
            {showQuantitySelector && (
                <>
                    <p className={classes.chooseQuantityText}>
                        {isVirtual ? '4' : '5'}. Choose quantity:
                    </p>

                    <div className={classes.incrementerContainer}>
                        <Incrementer
                            name={'quantity'}
                            onChange={(quantity) => {
                                setQuantity(parseInt(quantity))
                                validateField(
                                    'quantity',
                                    parseInt(quantity, 10)
                                )
                            }}
                            value={quantity}
                            min={1}
                            max={20}
                            incrementSize={1}
                            label={null}
                            containerClassName={'inline-block'}
                            directInput={true}
                            wrapperStyles={{ borderColor: '#868686' }}
                            buttonStyles={{
                                borderColor: '#868686',
                                color: '#868686',
                            }}
                            labelStyles={classes.incrementerText}
                            errors={errors}
                            moneyMode={false}
                        />

                        {!isEmpty(errors) && <></>}
                        <p
                            className={`${classes.bulkBuyCta} ${
                                // The error shifts this CTA, so this class keeps it aligned properly
                                !isEmpty(errors) ? classes.bulkBuyCtaError : ''
                            }`}
                        >
                            Interested in buying a larger quantity? <br />
                            <Link
                                className={classes.bulkBuyLink}
                                href={`${baseUrl}/gift-cards-for-business/`}
                            >
                                Buy in bulk
                            </Link>
                        </p>
                    </div>
                </>
            )}

            <Grid item xs={12}>
                {showApplyAllCheckbox && (
                    <div className={classes.applyAllCheckboxContainer}>
                        <ApplyAllCheckbox
                            applyToAll={applyCardDetailsToAll}
                            setApplyToAll={setApplyCardDetailsToAll}
                            labelText={
                                hasUrlData && !isVirtual
                                    ? 'Apply this greeting card design, amount, card design, card recipient and custom message to all gifts.'
                                    : 'Apply this gift card design, amount, packaging, and recipient information to all cards'
                            }
                            name={'landing'}
                        />
                    </div>
                )}
                <Button
                    id="landing-submit"
                    variant={'contained'}
                    size={'large'}
                    className={classes.nextButton}
                    aria-label="move to next step"
                    onClick={handleCustomCardFormSubmit}
                >
                    {isVirtual
                        ? 'Next: Customize Email'
                        : hasUrlData
                        ? 'Next: Shipping'
                        : 'Next: Select Mailer'}
                </Button>
                {isQuantityLimitExceeded() && (
                    <FormHelperText
                        className={classes.errorText}
                        error={isQuantityLimitExceeded()}
                    >
                        Only a total of {maxQuantity} build-a-card cards can be
                        added per order. Please reduce the quantity you have
                        selected
                        {customCards.length > 0 && (
                            <span>
                                {' '}
                                or remove some cards you've already created{' '}
                                <NavLink
                                    to={STEP_REVIEW.route}
                                    style={{ color: '#B71C1C' }}
                                >
                                    here
                                </NavLink>
                            </span>
                        )}
                        .
                    </FormHelperText>
                )}
                <DiscardItemAndReturnToReview />
                <Hidden mdUp>
                    <IntroContent baseUrl={baseUrl} />
                </Hidden>
                {deliveryMethod === CARD_TYPE.PLASTIC && (
                    <Hidden mdUp>
                        <ShippingInfoText />
                    </Hidden>
                )}
                <br />
                <br />
            </Grid>
        </>
    )
}

export default CustomCardForm

CustomCardForm.propTypes = {
    applyCardImageToAll: PropTypes.bool.isRequired,
    baseUrl: PropTypes.string.isRequired,
    cardNetwork: PropTypes.object.isRequired,
    cloudinaryId: PropTypes.string,
    defaultCardNetwork: PropTypes.object.isRequired,
    defaultDeliveryMethod: PropTypes.string.isRequired,
    deliveryMethod: PropTypes.string.isRequired,
    isVirtual: PropTypes.bool.isRequired,
    messageOnCard: PropTypes.string.isRequired,
    nameOnCard: PropTypes.string.isRequired,
    setImageError: PropTypes.func.isRequired,
    setMessageOnCard: PropTypes.func.isRequired,
    setNameOnCard: PropTypes.func.isRequired,
    setQuantity: PropTypes.func.isRequired,
    showApplyAllCheckbox: PropTypes.bool.isRequired,
    quantity: PropTypes.number.isRequired,
}
